import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import apiUrls from "../../../api";

interface UseGazeHeatmapCheckReturn {
  /** Map of sessionId -> hasGazeData */
  gazeDataAvailability: Record<number, boolean>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to batch-check which sessions have gaze heatmap data available.
 * Optimized for session listing pages - only checks for data presence, not content.
 *
 * @param sessionIds - Array of session IDs to check
 * @param enabled - Whether to fetch data
 */
export function useGazeHeatmapCheck(
  sessionIds: number[],
  enabled = true,
): UseGazeHeatmapCheckReturn {
  const [gazeDataAvailability, setGazeDataAvailability] = useState<Record<number, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track which session IDs we've already fetched to avoid re-fetching
  const fetchedIdsRef = useRef<Set<number>>(new Set());

  const fetchAvailability = useCallback(async () => {
    if (!enabled || sessionIds.length === 0) {
      return;
    }

    // Filter to only IDs we haven't fetched yet
    const idsToFetch = sessionIds.filter((id) => !fetchedIdsRef.current.has(id));

    if (idsToFetch.length === 0) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Batch in chunks of 100 (API limit)
      const chunks: number[][] = [];
      for (let i = 0; i < idsToFetch.length; i += 100) {
        chunks.push(idsToFetch.slice(i, i + 100));
      }

      const allResults: Record<number, boolean> = {};

      for (const chunk of chunks) {
        const response = await axios.get<Record<string, boolean>>(
          `${apiUrls.stats.gazeHeatmapCheck}?sessionIds=${chunk.join(",")}`,
        );

        // Convert string keys to numbers and merge results
        for (const [idStr, hasData] of Object.entries(response.data)) {
          const id = Number(idStr);
          allResults[id] = hasData;
          fetchedIdsRef.current.add(id);
        }
      }

      // Merge with existing results
      setGazeDataAvailability((prev) => ({
        ...prev,
        ...allResults,
      }));
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to check gaze data availability"));
    } finally {
      setIsLoading(false);
    }
  }, [sessionIds, enabled]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  return { gazeDataAvailability, isLoading, error };
}
